import { useToast } from "@/hooks/use-toast";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, CheckCircle, Clock, MapPin, ShoppingBag, Store, Bike, User, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useState } from "react";
import { OrderStatus, PaymentStatus, ORDER_STATUS_COLORS, ORDER_STATUS_LABELS, PAYMENT_STATUS_COLORS, PAYMENT_STATUS_LABELS } from "@/utils/orderStatus";
import { formatDate } from "@/utils/formatDate";

interface Customer {
  first_name: string;
  last_name: string;
  address: string;
  city: string;
  state: string;
  postal_code: string;
}

interface Meal {
  name: string;
}

interface Order {
  id: string;
  chef_id: string;
  chef_payout: number;
  created_at: string;
  delivery_address: string;
  delivery_fee: number;
  delivery_instructions: string;
  delivery_type: "pickup" | "delivery";
  foodie_id: string;
  meal_id: string;
  platform_fee: number;
  quantity: number;
  scheduled_date: string | null;
  special_instructions: string | null;
  status: OrderStatus;
  total_amount: number;
  transaction_id: string | null;
  updated_at: string;
  payment_status: PaymentStatus;
  payment_intent_id: string | null;
  payment_completed_at: string | null;
  customer: Customer;
  meals: Meal;
}

const OrderDetails = () => {
  const { id } = useParams();
  const { toast } = useToast();
  const [date, setDate] = useState<Date>();
  
  const { data: order, isLoading } = useQuery({
    queryKey: ['order', id],
    queryFn: async () => {
      const { data, error } = await supabase.from('orders').select(`
          *,
          customer:profiles!orders_foodie_id_fkey (
            first_name,
            last_name,
            address,
            city,
            state,
            postal_code
          ),
          meals (
            name
          )
        `).eq('id', id).single();
      
      if (error) throw error;
      
      console.log('Raw order from database:', data);
      console.log('Raw scheduled_date value:', data.scheduled_date);
      
      return data as Order;
    }
  });
  
  if (isLoading) {
    return <div>Loading...</div>;
  }
  
  if (!order) {
    return <div>Order not found.</div>;
  }
  
  if (order) {
    console.log('Order scheduled_date before formatting:', order.scheduled_date);
  }
  
  const scheduledDateFormatted = formatDate(order?.scheduled_date);
  const purchaseDateFormatted = formatDate(order?.created_at);
  const paymentCompletedDateFormatted = formatDate(order?.payment_completed_at);
  
  console.log('Formatted scheduled date:', scheduledDateFormatted);
  
  return <div className="container mx-auto px-4 py-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">Order #{id}</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Order Information</CardTitle>
            <CardDescription>Details about this order.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span>Status:</span>
              <Badge className={ORDER_STATUS_COLORS[order.status]}>
                {ORDER_STATUS_LABELS[order.status]}
              </Badge>
            </div>
            {order.payment_status && (
              <div className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                <span>Payment:</span>
                <Badge className={PAYMENT_STATUS_COLORS[order.payment_status]}>
                  {PAYMENT_STATUS_LABELS[order.payment_status]}
                </Badge>
                {paymentCompletedDateFormatted && (
                  <span className="text-xs text-muted-foreground">
                    ({paymentCompletedDateFormatted})
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-2">
              <ShoppingBag className="h-4 w-4" />
              <span>Purchase Date:</span>
              <span>{purchaseDateFormatted || 'Not available'}</span>
            </div>
            <div className="flex items-center gap-2">
              {order.delivery_type === 'delivery' ? <Bike className="h-4 w-4" /> : <Store className="h-4 w-4" />}
              <span>Delivery/Pickup:</span>
              <Badge variant="outline">
                {order.delivery_type === 'delivery' ? 'Delivery' : 'Pickup'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span>Customer:</span>
              <span>{order.customer?.first_name} {order.customer?.last_name}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              <span>Delivery Address:</span>
              <span>
                {order.customer?.address}, {order.customer?.city}, {order.customer?.state} {order.customer?.postal_code}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="h-4 w-4" />
              <span>Scheduled Date:</span>
              <span>{scheduledDateFormatted || 'Not specified'}</span>
            </div>
          </CardContent>
          <CardFooter>
            Total: ${order.total_amount}
          </CardFooter>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Meal Details</CardTitle>
            <CardDescription>Information about the ordered meal.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              <span>Meal:</span>
              <span>{order.meals?.name}</span>
            </div>
            <div>
              <span>Quantity: </span>
              <span>{order.quantity}</span>
            </div>
            <div>
              <span>Special Instructions: </span>
              <span>{order.special_instructions || 'None'}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
};

export default OrderDetails;
